import React, { useState } from "react";
import { CaretUp, CaretDown, CaretLeft, CaretRight, Lightning, Compass } from "@phosphor-icons/react";
import { station, degToRad } from "../lib/api";
import { toast } from "sonner";

export const Joystick = ({ disabled }) => {
  const [fast, setFast] = useState(false);
  const [hzInput, setHzInput] = useState("");
  const [vInput, setVInput] = useState("");
  const [busy, setBusy] = useState(false);

  const nudge = async (dir) => {
    if (disabled) return;
    try {
      await station.nudge(dir, fast);
    } catch (e) {
      toast.error("Nudge failed");
    }
  };

  const turnTo = async () => {
    if (disabled) return;
    const payload = {};
    if (hzInput.trim() !== "") payload.hz = degToRad(parseFloat(hzInput));
    if (vInput.trim() !== "") payload.v = degToRad(parseFloat(vInput));
    if (Object.keys(payload).length === 0) return;
    setBusy(true);
    try {
      await station.turn(payload);
      toast.success("Slewing to target");
    } catch (e) {
      toast.error("Turn failed");
    } finally {
      setBusy(false);
    }
  };

  const DPad = ({ dir, icon: Icon, testid }) => (
    <button
      data-testid={testid}
      onClick={() => nudge(dir)}
      disabled={disabled}
      className="btn-tac flex items-center justify-center"
    >
      <Icon size={28} weight="bold" />
    </button>
  );

  return (
    <div className="panel" data-testid="joystick-panel">
      <div className="panel-header">
        <span>MANUAL AIM</span>
        <button
          data-testid="joystick-speed-toggle"
          onClick={() => setFast((v) => !v)}
          className={`flex items-center gap-1 rounded-sm border px-2 py-1 text-[10px] tracking-widest uppercase ${
            fast
              ? "border-[var(--amber)] text-[var(--amber)]"
              : "border-[var(--border)] text-[var(--text-dim)]"
          }`}
        >
          <Lightning size={12} weight="bold" />
          {fast ? "FAST" : "FINE"}
        </button>
      </div>

      <div className="p-4">
        <div className="mx-auto grid w-56 grid-cols-3 gap-2">
          <div />
          <DPad dir="up" icon={CaretUp} testid="joystick-up-button" />
          <div />
          <DPad dir="left" icon={CaretLeft} testid="joystick-left-button" />
          <div className="flex h-16 items-center justify-center rounded-sm border border-[var(--border-strong)] bg-[var(--surface-2)]">
            <Compass size={22} weight="duotone" className="text-[var(--cyan)]" />
          </div>
          <DPad dir="right" icon={CaretRight} testid="joystick-right-button" />
          <div />
          <DPad dir="down" icon={CaretDown} testid="joystick-down-button" />
          <div />
        </div>

        <div className="mt-5 grid grid-cols-2 gap-2">
          <NumField label="Turn to Hz°" testid="turn-hz-input" value={hzInput} onChange={setHzInput} />
          <NumField label="Turn to V°" testid="turn-v-input" value={vInput} onChange={setVInput} />
        </div>
        <button
          data-testid="turn-to-btn"
          onClick={turnTo}
          disabled={disabled || busy || (!hzInput && !vInput)}
          className="btn-tac mt-3 w-full"
          data-variant="primary"
        >
          {busy ? "SLEWING…" : "TURN TO"}
        </button>
      </div>
    </div>
  );
};

const NumField = ({ label, testid, value, onChange }) => (
  <label className="block">
    <span className="mb-1 block text-[10px] uppercase tracking-widest text-[var(--text-dim)]">{label}</span>
    <input
      data-testid={testid}
      type="number"
      step="0.001"
      inputMode="decimal"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder="—"
      className="readout w-full rounded-sm border border-[var(--border-strong)] bg-[var(--bg)] px-3 py-3 text-lg tabular-nums text-[var(--text)] focus:border-[var(--neon)]"
    />
  </label>
);
